import { Box, Button, Chip, Grid, Pagination, Paper, Skeleton, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLang } from '../../../../language';
import { InputText } from '../../../../components/Inputs/InputText';
import { CursosServices } from '../../controllers/services/cursos.services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faGraduationCap, faTags } from '@fortawesome/free-solid-svg-icons';
import { ModalCurso } from './ModalCurso';

/**
 * Convierte HTML (la descripción llega con etiquetas) a texto plano para el
 * resumen de la tarjeta del listado. Quita las etiquetas (sustituyéndolas por
 * espacio para no pegar palabras) y decodifica las entidades HTML
 * (&agrave;, &nbsp;, &middot;...) mediante DOMParser (inerte: no ejecuta
 * scripts ni carga recursos).
 */
const textoPlano = (html: string) => {

    const conEspacios = String(html || '').replace(/<[^>]*>/g, ' ')

    const doc = new DOMParser().parseFromString(conEspacios, 'text/html')

    return (doc.body?.textContent || '')
        .replace(/\s+/g, ' ')
        .trim()
}

export const CatalogoCursos = ({ setBreadcrumbLineItems, route }: any) => {

    const Lang = useLang()

    const [cursoSeleccionado, setCursoSeleccionado] = useState<any>(null)

    const [filtroCursos, setFiltroCursos] = useState('')

    // Marca el primer render: la carga inicial la hace el efecto de montaje y el
    // debounce se salta su primera ejecución para no duplicar la petición.
    const primeraCargaCursos = useRef(true)

    // Página seleccionada del catálogo: se mantiene en estado local para que quede
    // marcada mientras el WS responde (durante la carga `data` vuelve a null).
    const [paginacionCursosView, setPaginacionCursosView] = useState({ page: 1, last_page: 1 })

    // Catàleg de cursos del WS d'Activitats (integraWeb.php accion=obtener_cursos)
    const [fetchCursosGET, fetchCursosGETData] = CursosServices.GET_CURSOS()

    const cursos = fetchCursosGETData.data?.items || []

    const cursosCargando = !!fetchCursosGETData.loading

    const cursosError = fetchCursosGETData.error

    const [fetchCursoGET, fetchCursoGETData, resetCursoGET] = CursosServices.GET_CURSO()

    useEffect(() => {

        setBreadcrumbLineItems((item: any) => [...item.slice(0, 2), route])

    }, [])

    // Carga inicial inmediata: la primera petición no debe esperar al debounce
    // del buscador (evita el retardo antes de que aparezca el skeleton).
    useEffect(() => {

        fetchCursosGET({ actividad: '', page: 1 })

    }, [])

    // Sincroniza la página seleccionada con la respuesta del WS
    useEffect(() => {

        const paginacion = fetchCursosGETData.data?.paginacion

        if (paginacion?.page != null) {

            // `Number()`: el WS devuelve `current_page` como string; MUI compara
            // `page` con `===` estricto y sin coerción se desmarcaría el botón.
            setPaginacionCursosView({
                page: Number(paginacion.page),
                last_page: Number(paginacion.last_page || paginacion.page),
            })
        }

    }, [fetchCursosGETData.data])

    // Búsqueda de cursos con debounce: se llama al WS con `actividad` y `page`
    useEffect(() => {

        if (primeraCargaCursos.current) {

            primeraCargaCursos.current = false
            return
        }

        const handler = setTimeout(() => {

            setPaginacionCursosView((prev: any) => ({ ...prev, page: 1 }))

            fetchCursosGET({ actividad: filtroCursos.trim(), page: 1 })
        }, 400)

        return () => clearTimeout(handler)

    }, [filtroCursos])

    const handleCambioPaginaCursos = (event: any, nuevaPagina: number) => {

        setPaginacionCursosView((prev: any) => ({ ...prev, page: nuevaPagina }))

        fetchCursosGET({ actividad: filtroCursos.trim(), page: nuevaPagina })
    }

    const handleVerCurso = (curso: any) => {

        setCursoSeleccionado(curso)

        // GET .../integraWeb.php?accion=mostrar_detalle_curso&id=N (contenido completo del modal)
        fetchCursoGET({ id: curso.id })
    }

    const handleCerrarCurso = () => {

        setCursoSeleccionado(null)
        resetCursoGET()
    }

    return (
        <div>

            <Paper className='p-3 p-md-4' sx={{ borderRadius: 2.5 }}>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} className='mb-2'>
                    <FontAwesomeIcon icon={faGraduationCap} className='color-primary' />
                    <Typography variant='h6' className='my-0 fw-500 color-primary'>
                        {Lang('CATALOGO_DE_CURSOS')}
                    </Typography>
                </Box>

                <Box className='mb-2'>
                    <InputText
                        label={Lang('BUSCAR_CURSO')}
                        name='buscarCurso'
                        value={filtroCursos}
                        onChange={(e: any) => setFiltroCursos(e.target.value)}
                    />
                </Box>

                {
                    cursosCargando &&
                    <Grid container spacing={1.5} className='mb-2'>
                        {
                            [0, 1, 2, 3, 4, 5].map((indice: number) => (
                                <Grid item xs={12} sm={6} lg={4} key={indice} sx={{ display: 'flex' }}>
                                    <Paper variant="outlined" className='h-100' sx={{ width: '100%', p: 1.5, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                            <Skeleton variant="text" width="60%" sx={{ fontSize: '0.9rem' }} />
                                            <Skeleton variant="rounded" width={64} height={22} />
                                        </Box>
                                        <Skeleton variant="rounded" width={96} height={22} sx={{ mt: 1 }} />
                                        <Skeleton variant="text" sx={{ fontSize: '0.8rem', mt: 1 }} />
                                        <Skeleton variant="text" sx={{ fontSize: '0.8rem' }} />
                                        <Skeleton variant="text" width="70%" sx={{ fontSize: '0.8rem' }} />
                                        <Box sx={{ textAlign: 'right', mt: 1 }}>
                                            <Skeleton variant="text" width={90} sx={{ display: 'inline-block' }} />
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))
                        }
                    </Grid>
                }

                {
                    !cursosCargando && cursosError &&
                    <Typography variant='caption' className='my-0 mb-2' sx={{ color: 'error.main', display: 'block', textAlign: 'center' }}>
                        {String(cursosError?.message || cursosError)}
                    </Typography>
                }

                <Grid container spacing={1.5}>

                    {
                        fetchCursosGETData.loading === false && !cursosError && cursos.length === 0 && filtroCursos.trim() !== '' &&
                        <Grid item xs={12}>
                            <Typography variant='body2' className='my-0' sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                                {Lang('SENSE_CURSOS')}
                            </Typography>
                        </Grid>
                    }

                    {
                        cursos.map((curso: any) => (
                            <Grid item xs={12} sm={6} lg={4} key={curso.id} sx={{ display: 'flex' }}>
                                <Paper
                                    variant="outlined"
                                    className='h-100'
                                    sx={{
                                        width: '100%',
                                        p: 1.5,
                                        borderRadius: 2,
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            boxShadow: 2,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                        <Typography variant='subtitle2' className='fw-500 my-0' sx={{ wordBreak: 'break-word', minWidth: 0 }}>
                                            {curso.titulo}
                                        </Typography>
                                        <Chip
                                            icon={<FontAwesomeIcon icon={faTags} style={{ fontSize: 11 }} />}
                                            label={curso.categoria}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Box>

                                    {
                                        curso.temas?.length > 0 &&
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                                            {
                                                curso.temas.slice(0, 2).map((tema: string, indice: number) => (
                                                    <Chip key={indice} label={tema} size="small" />
                                                ))
                                            }
                                            {
                                                curso.temas.length > 2 &&
                                                <Chip label={`+${curso.temas.length - 2}`} size="small" variant="outlined" />
                                            }
                                        </Box>
                                    }

                                    {
                                        curso.descripcion &&
                                        <Typography
                                            variant='body2'
                                            className='my-1'
                                            sx={{
                                                color: 'text.secondary',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {textoPlano(curso.descripcion)}
                                        </Typography>
                                    }

                                    <Box sx={{ textAlign: 'right' }}>
                                        <Button
                                            size="small"
                                            color="primary"
                                            variant="text"
                                            startIcon={<FontAwesomeIcon icon={faEye} />}
                                            onClick={() => handleVerCurso(curso)}
                                        >
                                            {Lang('VER_DETALLE')}
                                        </Button>
                                    </Box>

                                </Paper>
                            </Grid>
                        ))
                    }

                </Grid>

                {
                    fetchCursosGETData.loading !== null && (cursos.length > 0 || fetchCursosGETData.loading === true) &&
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant='caption' className='my-0' sx={{ color: 'text.secondary' }}>
                            {paginacionCursosView.page} de {paginacionCursosView.last_page}
                        </Typography>
                        {
                            paginacionCursosView.last_page > 1 &&
                            <Pagination
                                page={paginacionCursosView.page}
                                count={paginacionCursosView.last_page}
                                onChange={handleCambioPaginaCursos}
                                color="primary"
                                size="small"
                            />
                        }
                    </Box>
                }

            </Paper>

            {/* -------------------- Modal de curs -------------------- */}

            <ModalCurso
                curso={cursoSeleccionado}
                detalle={fetchCursoGETData.data?.data}
                cargando={!!fetchCursoGETData.loading}
                onClose={handleCerrarCurso}
            />

        </div>
    )
}